import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import API from "../../../../services/request";
import { API_PATH, PATH } from "../../../../utils/config";
import GallerySkeleton from "../../../Gallery/GallerySkeleton";
import { collectionDeepLink } from "./collectionSearch";
import { toAbsoluteMediaUrl, WHEEL_SHARE_IMAGE_FALLBACK } from "../../../ShareModal/toAbsoluteMediaUrl";

// BUG-953: a Collection's shareable link (`/collection/:collectionId`) only
// carries the folder id -- the gallery route that actually opens a collection
// (`/gallery/:id?collection=<id>`, already consumed by OwnerView/GuestView)
// also needs the OWNER's id. This resolves the owner via
// GET /api/v2/folders/<id>/ (the same detail endpoint UPDATE_FOLDER PATCHes)
// and redirects once, so no new "open a folder" mechanism is needed on either
// side -- just this one missing hop from a bare folder id to the real route.
//
// fix-kelly0927-collection-share-link-numeric-id: this used to hand the
// RAW numeric `collectionId` (from the URL param) to `collectionDeepLink`,
// producing `?collection=<numeric id>`. GuestView/OwnerView's `?collection=`
// consumption has two independent readers: a numeric match against the
// visitor's already-loaded top-level PUBLIC `collections` chip list (a
// one-shot ref -- never retried, and blind to private/nested/timing-raced
// folders), and a dedicated slug reader that calls the unauthenticated
// `GET /api/v2/folders/shared/<slug>/` endpoint (works for Public AND
// Private alike, per copyShareLink's own share_slug-based link -- see
// OwnerView.jsx). Sending the numeric id meant this Share-dialog link relied
// entirely on the fragile chip-list match: MEASURED live on web-stg
// (main.a9e72116.js) that a freshly-created Public collection landed a guest
// on the fully unfiltered gallery with the collection's own wheels never
// rendering -- the numeric id is not a valid share slug, so the dedicated
// resolver 404s, and the chip-list match silently failed too. The very same
// `GET_FOLDER`/`UPDATE_FOLDER` response already carries `share_slug`
// (copyShareLink reads it from this identical endpoint) -- forward that
// instead so this link takes the same reliable slug-resolution path the
// kebab's "Copy share link" already uses. Falls back to the raw id only if
// a response somehow omits `share_slug`, matching the prior behaviour.
const CollectionRedirect = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  // fix-kelly-0929-collections-video-audit item 10: MEASURED live on web-dev —
  // this route never carried its own OG tags, so a social crawler hitting
  // `/collection/<id>` (this exact URL, share_slug or numeric id) only ever
  // saw public/index.html's STATIC fallback tags — generic "iYuga - Your
  // Personalized App" title/image, and (the concrete, confirmed-wrong part)
  // `og:url` hardcoded to `https://iyuga.net`, a DIFFERENT domain than the
  // one actually serving the page (web-dev/web-stg/web.iyuga.net). A crawler
  // that reads `og:url` for canonicalization is being told this page IS
  // iyuga.net, which is a plausible cause of Facebook's sharer flagging the
  // real URL "not eligible". Mirror ViewWheel's existing (working) per-page
  // Helmet override pattern here: real title/description/image/url for the
  // actual collection, set as soon as its data resolves.
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    API.get(API_PATH.UPDATE_FOLDER(collectionId))
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data;
        const ownerId = data?.user;
        const shareSlug = data?.share_slug;
        setMeta({
          title: data?.name ? `${data.name} | iYuga` : "iYuga Collection",
          description:
            data?.description ||
            "Check out this collection on iYuga.",
          image: toAbsoluteMediaUrl(data?.cover_wheel?.wheel_image) || WHEEL_SHARE_IMAGE_FALLBACK,
          url: window.location.href,
        });
        navigate(
          ownerId != null
            ? collectionDeepLink(shareSlug || collectionId, ownerId)
            : PATH.GALLERY,
          { replace: true }
        );
      })
      .catch(() => {
        // Private/deleted/not-found collection: land on the gallery root
        // rather than a dead end, same fallback LegacyProfileRedirect uses.
        if (!cancelled) navigate(PATH.GALLERY, { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [collectionId, navigate]);

  return (
    <>
      {meta && (
        <HelmetProvider>
          <Helmet>
            <title>{meta.title}</title>
            <meta name="description" content={meta.description} />
            <meta property="og:title" content={meta.title} />
            <meta property="og:description" content={meta.description} />
            <meta property="og:image" content={meta.image} />
            <meta property="og:url" content={meta.url} />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="iYuga" />
          </Helmet>
        </HelmetProvider>
      )}
      <GallerySkeleton />
    </>
  );
};

export default CollectionRedirect;
